/**
 * The screen handle of the store-setup flow (plan §11.2, the reservations
 * guide pattern): every screen exposes `{ bar, primary, secondary, back }`
 * with `defineExpose` and the page binds its single `V2SetupBottomBar` to the
 * active screen (Vue 2.7 unwraps the exposed `bar` ref on the instance).
 */

/** What the bottom bar shows for the active screen. */
export interface IFlowBarState {
  /** «Atrás» (ghost, arrow icon; the label hides on phones). Handled by `back()`. */
  backLabel: string | null;
  /** One line of grey text next to the CTA; null hides it. */
  helper: string | null;
  /** The helper is a link doing what the CTA does (e.g. «Complete 3 precios…» opens the first dish to fix). */
  helperIsLink: boolean;
  /** Neutral outline action next to the CTA. Handled by `secondary()`. */
  secondaryLabel: string | null;
  /** The single orange CTA; null hides it. Handled by `primary()`. */
  primaryLabel: string | null;
  primaryDisabled: boolean;
  /** Keeps the orange style and still fires `primary()` (e.g. «Publicar menú» while a dish needs a price). */
  primaryAriaDisabled: boolean;
  primaryBusy: boolean;
  /**
   * A ghost action on the left other than «Atrás» (e.g. «Configurar después»).
   * Also handled by `back()`: a screen sets `backLabel` or `leftLabel`, never both.
   */
  leftLabel: string | null;
}

/** The instance of the active screen as the page sees it through its template ref. */
export interface IFlowScreenHandle {
  bar: IFlowBarState;
  /** Work still being saved (uploads, draft writes): the page's leave guard asks before leaving. Absent = nothing. */
  busy?: boolean;
  /**
   * A screen with screens of its own (the menu flow: source ↔ workspace)
   * names the current one, so the shell scrolls up and focuses its heading
   * when it changes too.
   */
  innerKey?: string;
  primary(): Promise<void>;
  secondary(): Promise<void>;
  back(): Promise<void>;
}

/** A bar with nothing on it (no screen yet, or a gate card that carries its own buttons). */
export const EMPTY_FLOW_BAR: Readonly<IFlowBarState> = Object.freeze({
  backLabel: null,
  helper: null,
  helperIsLink: false,
  secondaryLabel: null,
  primaryLabel: null,
  primaryDisabled: false,
  primaryAriaDisabled: false,
  primaryBusy: false,
  leftLabel: null,
});

/** A full bar state from the fields a screen sets. */
export function flowBar(state: Partial<IFlowBarState>): IFlowBarState {
  return { ...EMPTY_FLOW_BAR, ...state };
}

/** Nothing to show (no screen yet, a gate card, the AI switched off): the bar is hidden, not an empty strip. */
export function flowBarIsEmpty(state: IFlowBarState): boolean {
  return (
    state.backLabel === null &&
    state.leftLabel === null &&
    state.helper === null &&
    state.secondaryLabel === null &&
    state.primaryLabel === null
  );
}
